(() => {
  'use strict';
  if (window.STHBrowsingHistory) return;
  const KEY = 'recentlyViewedProduct';
  const EVENT = 'sth:browsing-history-change';
  let storageBlocked = false;
  const productURL = value => {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || url.protocol !== 'https:' || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
      url.search = ''; url.hash = ''; url.pathname = url.pathname.replace(/\/$/, '');
      return url;
    } catch (_) { return null; }
  };
  function record(value) {
    if (!value || !/^\d+$/.test(String(value.productId || ''))) return null;
    const url = productURL(value.productUrl);
    return url ? { productId: String(value.productId), productUrl: url.pathname } : null;
  }
  function read() {
    let values;
    try { values = JSON.parse(localStorage.getItem(KEY) || '[]'); storageBlocked = false; }
    catch (error) { storageBlocked = !(error instanceof SyntaxError); return []; }
    if (!Array.isArray(values)) return [];
    const ids = new Set(), paths = new Set(), result = [];
    for (const value of values.slice(-24).reverse()) {
      const item = record(value);
      if (!item || ids.has(item.productId) || paths.has(item.productUrl)) continue;
      ids.add(item.productId); paths.add(item.productUrl); result.push(item);
      if (result.length === 12) break;
    }
    return result.reverse();
  }
  function changed() { document.dispatchEvent(new CustomEvent(EVENT)); }
  function write(values, clear = false) {
    try {
      if (clear) localStorage.removeItem(KEY); else localStorage.setItem(KEY, JSON.stringify(values));
      storageBlocked = false;
    } catch (_) { storageBlocked = true; return false; }
    changed(); return true;
  }
  function remember(value) {
    const item = record(value);
    if (!item) return false;
    const values = read(); if (storageBlocked) return false;
    return write([...values.filter(entry => entry.productId !== item.productId && entry.productUrl !== item.productUrl), item].slice(-12));
  }
  function remove(value) {
    const ids = new Set((Array.isArray(value) ? value : [value]).map(String));
    const values = read(); if (storageBlocked) return false;
    return write(values.filter(item => !ids.has(item.productId)));
  }
  function clear() { return write([], true); }
  window.STHBrowsingHistory = Object.freeze({ read, remember, remove, clear });
  window.addEventListener('storage', event => { if (event.key === KEY || event.key === null) changed(); });

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const textKey = value => String(value || '').normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
  const departmentKey = value => typeof value === 'string' ? value.normalize('NFKC').trim().toLowerCase() : '';
  function initialize() {
    const dialog = document.querySelector('[data-sth-history-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.dataset.sthHistoryReady) return;
    dialog.dataset.sthHistoryReady = 'true';
    const list = dialog.querySelector('[data-sth-history-list]');
    const status = dialog.querySelector('[data-sth-history-status]');
    const empty = dialog.querySelector('[data-sth-history-empty]');
    const clearButton = dialog.querySelector('[data-sth-history-clear]');
    const retry = dialog.querySelector('[data-sth-history-retry]');
    const closeButton = dialog.querySelector('[data-sth-history-close]');
    if (!list || !status || !empty || !clearButton || !closeButton) return;
    const currency = dialog.dataset.currency || 'USD', locale = dialog.dataset.locale || document.documentElement.lang || 'en-US';
    const rootPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(dialog.dataset.rootUrl || '') ? dialog.dataset.rootUrl : '/';
    const departments = (() => {
      try {
        const values = JSON.parse(dialog.querySelector('[data-sth-history-departments]')?.textContent || '[]');
        if (!Array.isArray(values)) return [];
        const handles = new Set();
        return values.map(value => {
          if (!value || typeof value.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(value.handle) || handles.has(value.handle) || typeof value.label !== 'string' || !value.label.trim() || !Array.isArray(value.rules) || !value.rules.length) throw new Error('invalid-department');
          handles.add(value.handle);
          const rules = value.rules.map(rule => {
            if (!rule || !['product_type', 'tag'].includes(rule.field) || typeof rule.value !== 'string' || !departmentKey(rule.value)) throw new Error('invalid-department-rule');
            return { field: rule.field, value: departmentKey(rule.value) };
          });
          return { handle: value.handle, label: value.label.trim(), rules };
        });
      } catch (_) { return []; }
    })();
    let generation = 0, opener, focusId, focusIndex = 0;
    const controllers = new Set(), cache = new Map(), groups = new Map();
    const announce = message => { status.textContent = message; };
    const money = value => {
      try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value / 100); }
      catch (_) { return `${currency} ${(value / 100).toFixed(2)}`; }
    };
    const expose = () => document.querySelectorAll('[data-sth-history-open]').forEach(button => { button.hidden = false; });
    expose();
    new MutationObserver(mutations => {
      if (mutations.some(mutation => [...mutation.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-sth-history-open]') || node.querySelector('[data-sth-history-open]'))))) expose();
    }).observe(document.body, { subtree: true, childList: true });
    document.addEventListener('shopify:section:load', expose);
    const cancel = () => { generation++; controllers.forEach(controller => controller.abort()); controllers.clear(); };

    async function fetchProduct(item) {
      if (cache.has(item.productId)) return cache.get(item.productId);
      const savedURL = productURL(item.productUrl), controller = new AbortController();
      const url = savedURL && productURL(rootPath + 'products/' + savedURL.pathname.split('/').pop());
      if (!url) return { missing: true };
      url.pathname += '.js'; controllers.add(controller);
      const timeout = setTimeout(() => controller.abort(), 8000);
      let result;
      try {
        const response = await fetch(url.href, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (response.status === 404) result = { missing: true };
        else {
          if (!response.ok) throw new Error('unavailable');
          const value = await response.json(), path = productURL(value.url || item.productUrl);
          const min = Number(value.price_min ?? value.price), max = Number(value.price_max ?? value.price);
          if (String(value.id) !== item.productId || !path || typeof value.title !== 'string' || !value.title.trim() || !Number.isFinite(min) || min < 0 || !Number.isFinite(max) || max < min) throw new Error('invalid-product');
          path.pathname = rootPath + 'products/' + path.pathname.split('/').pop();
          let image = '';
          const imageValue = value.featured_image?.url || value.featured_image || value.images?.[0];
          if (typeof imageValue === 'string' && imageValue) {
            try { const candidate = new URL(imageValue, location.origin); if (candidate.protocol === 'https:') { candidate.searchParams.set('width', '480'); image = candidate.href; } } catch (_) {}
          }
          result = { product: { id: item.productId, title: value.title.trim(), url: path.href, handle: path.pathname.split('/').pop(), image, imageKey: image ? new URL(image).pathname : '', min, max, available: value.available === true, type: typeof value.type === 'string' ? value.type : '', tags: Array.isArray(value.tags) ? value.tags.filter(tag => typeof tag === 'string') : [] } };
        }
        if (!controller.signal.aborted) cache.set(item.productId, result);
        return result;
      } catch (_) { return { failed: true }; }
      finally { clearTimeout(timeout); controllers.delete(controller); }
    }
    function card(item, value) {
      const product = value.product, li = element('li', 'sth-history-card');
      li.dataset.productId = item.productId;
      if (product) {
        const imageLink = element('a', 'sth-history-image'); imageLink.href = product.url; imageLink.tabIndex = -1; imageLink.setAttribute('aria-hidden', 'true');
        if (product.image) {
          const image = element('img'); image.src = product.image; image.alt = ''; image.width = 240; image.height = 240; image.loading = 'lazy';
          image.addEventListener('error', () => { image.hidden = true; imageLink.append(element('span', '', 'View product')); }, { once: true }); imageLink.append(image);
        } else imageLink.append(element('span', '', 'View product'));
        const title = element('h4'), link = element('a', '', product.title); link.href = product.url; title.append(link);
        li.append(imageLink, title, element('p', 'sth-history-price', product.min === product.max ? money(product.min) : `${money(product.min)}–${money(product.max)}`));
        if (!product.available) li.append(element('p', 'sth-history-availability', 'Currently unavailable'));
      } else {
        li.append(element('h4', '', value.missing ? 'Product unavailable' : 'Unable to load product'));
        li.append(element('p', 'sth-history-availability', value.missing ? 'This product is no longer available.' : 'Try again to see the latest product details.'));
      }
      const actions = element('div', 'sth-history-actions');
      if (product) {
        const save = element('button', 'sth-history-save'); save.type = 'button'; save.hidden = true;
        save.dataset.sthSaveHandle = product.handle; save.dataset.sthSaveTitle = product.title; save.dataset.sthSaveUrl = product.url; save.dataset.sthSaveImage = product.image;
        const label = element('span', '', 'Save item'); label.dataset.sthSaveLabel = ''; save.append(label); actions.append(save);
      }
      const removeButton = element('button', '', 'Remove'); removeButton.type = 'button'; removeButton.dataset.sthHistoryRemove = item.productId;
      removeButton.setAttribute('aria-label', 'Remove ' + (product?.title || 'this product') + ' from browsing history'); actions.append(removeButton);
      li.append(actions); return li;
    }
    function departmentFor(product) {
      if (!product) return null;
      const type = departmentKey(product.type), tags = new Set(product.tags.map(departmentKey));
      return departments.find(department => department.rules.some(rule => rule.field === 'product_type' && rule.value === type)) ||
        departments.find(department => department.rules.some(rule => rule.field === 'tag' && tags.has(rule.value))) || null;
    }
    function departmentSection(department, cards, index) {
      const section = element('section', 'sth-history-department');
      section.dataset.sthHistoryDepartment = department?.handle || 'other';
      const heading = element('header', 'sth-history-department-heading');
      const title = element('h3', '', department?.label || 'More recently viewed');
      title.id = 'sth-history-department-' + generation + '-' + index;
      section.setAttribute('aria-labelledby', title.id);
      heading.append(title, element('p', '', `${cards.length} ${cards.length === 1 ? 'product' : 'products'}`));
      if (department) {
        const link = element('a', 'sth-history-department-link', 'Shop this department');
        link.dataset.sthHistoryDepartmentLink = ''; link.href = rootPath + 'collections/' + department.handle;
        link.setAttribute('aria-label', 'Shop ' + department.label); heading.append(link);
      }
      const grid = element('ul', 'sth-history-grid'); grid.append(...cards);
      section.append(heading, grid); return section;
    }
    async function render() {
      if (!dialog.open) return;
      cancel(); const current = generation, entries = read().reverse();
      list.replaceChildren(); groups.clear(); empty.hidden = true; retry.hidden = true;
      clearButton.hidden = !entries.length || storageBlocked;
      if (storageBlocked) { announce('Browsing history is unavailable because browser storage is blocked.'); return; }
      if (!entries.length) { empty.hidden = false; announce('No recently viewed products.'); if (focusId) closeButton.focus(); focusId = null; return; }
      announce('Loading your recently viewed products…');
      const values = new Array(entries.length); let cursor = 0;
      await Promise.all(Array.from({ length: Math.min(4, entries.length) }, async () => {
        while (cursor < entries.length && current === generation && dialog.open) {
          const index = cursor++; values[index] = await fetchProduct(entries[index]);
        }
      }));
      if (current !== generation || !dialog.open || !dialog.isConnected) return;
      const ids = new Map(), titles = new Map(), images = new Map(), cards = [];
      entries.forEach((item, index) => {
        const value = values[index] || { failed: true }, product = value.product;
        const duplicate = product && (ids.get(product.id) || titles.get(textKey(product.title)) || (product.imageKey && images.get(product.imageKey)));
        if (duplicate) { groups.get(duplicate).push(item.productId); return; }
        groups.set(item.productId, [item.productId]);
        if (product) { ids.set(product.id, item.productId); titles.set(textKey(product.title), item.productId); if (product.imageKey) images.set(product.imageKey, item.productId); }
        cards.push({ item, value });
      });
      const departmentCards = new Map(), otherCards = [];
      cards.forEach(({ item, value }) => {
        const department = departmentFor(value.product), node = card(item, value);
        if (!department) { otherCards.push(node); return; }
        if (!departmentCards.has(department)) departmentCards.set(department, []);
        departmentCards.get(department).push(node);
      });
      const sections = [...departmentCards].map(([department, nodes], index) => departmentSection(department, nodes, index));
      if (otherCards.length) sections.push(departmentSection(null, otherCards, sections.length));
      list.replaceChildren(...sections);
      retry.hidden = !values.some(value => value?.failed);
      announce(`${cards.length} recently viewed ${cards.length === 1 ? 'product' : 'products'}.${retry.hidden ? '' : ' Some details could not be loaded.'}`);
      if (focusId) {
        const buttons = [...list.querySelectorAll('[data-sth-history-remove]')];
        (buttons[Math.min(focusIndex, buttons.length - 1)] || closeButton).focus({ preventScroll: true }); focusId = null;
      }
    }
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-sth-history-open]');
      if (!trigger) return;
      event.preventDefault(); opener = trigger; cache.clear();
      const nav = trigger.closest('#nav');
      if (nav) {
        if (typeof window.close_mobile_nav === 'function') window.close_mobile_nav();
        else {
          const closeNav = nav.querySelector('.close-nav');
          if (closeNav) {
            const closeEvent = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
            closeEvent.preventDefault(); closeNav.dispatchEvent(closeEvent);
          }
        }
      }
      if (!dialog.open) dialog.showModal();
      closeButton.focus({ preventScroll: true }); render();
    });
    closeButton.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => {
      if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }
      const button = event.target.closest('[data-sth-history-remove]');
      if (!button) return;
      focusId = button.dataset.sthHistoryRemove;
      focusIndex = [...list.querySelectorAll('[data-sth-history-remove]')].indexOf(button);
      if (!remove(groups.get(focusId) || focusId)) { focusId = null; announce('We could not remove this product. Browser storage may be unavailable.'); }
    });
    dialog.addEventListener('close', () => {
      cancel(); focusId = null;
      const visible = node => node?.isConnected && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden';
      const target = visible(opener) ? opener : [...document.querySelectorAll('[aria-controls="nav"]')].find(visible);
      target?.focus({ preventScroll: true });
    });
    clearButton.addEventListener('click', () => {
      focusId = 'clear';
      if (!clear()) { focusId = null; announce('We could not clear browsing history. Browser storage may be unavailable.'); }
    });
    retry.addEventListener('click', () => { render(); });
    document.addEventListener(EVENT, () => { if (dialog.open) render(); });
    window.addEventListener('pageshow', event => { if (event.persisted) { cache.clear(); if (dialog.open) render(); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true }); else initialize();
})();
